// src/components/admin/FundRaisingAdminPanel.tsx
// DROP 18d — Admin management of fund raising campaigns and donations

import React, { useState, useEffect, useCallback } from "react";
import {
  Target, Heart, Gift, TrendingUp, Plus, Edit2,
  Trash2, CheckCircle, XCircle, RefreshCw,
  AlertTriangle, ToggleLeft, ToggleRight, Download
} from "lucide-react";

interface Campaign {
  id: string;
  title: string;
  description: string;
  type: "general"|"event"|"item"|"village";
  targetAmount: number;
  raisedAmount: number;
  currency: string;
  deadline?: string;
  isActive: boolean;
  itemsList?: string[];
  createdAt: string;
}

interface Donation {
  id: string;
  campaignId: string;
  donorName: string;
  donorEmail: string;
  donorType: string;
  donorVillage?: string;
  amount: number;
  currency: string;
  message?: string;
  dedication?: string;
  isAnonymous: boolean;
  status: "confirmed"|"pending"|"failed";
  reference: string;
  donatedAt: string;
}

interface Pledge {
  id: string;
  campaignId: string;
  pledgeType: "amount"|"item";
  amount?: number;
  item?: string;
  name: string;
  email: string;
  fulfillBy?: string;
  status: "pending"|"fulfilled"|"cancelled";
  pledgedAt: string;
}

type TabType = "campaigns"|"donations"|"pledges";

export default function FundRaisingAdminPanel() {
  const [tab,       setTab]       = useState<TabType>("campaigns");
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [donations, setDonations] = useState<Donation[]>([]);
  const [pledges,   setPledges]   = useState<Pledge[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [msg,       setMsg]       = useState<{type:"ok"|"err";text:string}|null>(null);
  const [showForm,  setShowForm]  = useState(false);
  const [editCamp,  setEditCamp]  = useState<Campaign|null>(null);

  // Campaign form
  const [cTitle,    setCTitle]    = useState("");
  const [cDesc,     setCDesc]     = useState("");
  const [cType,     setCType]     = useState<Campaign["type"]>("general");
  const [cTarget,   setCTarget]   = useState("");
  const [cCurrency, setCCurrency] = useState("NGN");
  const [cDeadline, setCDeadline] = useState("");
  const [cActive,   setCActive]   = useState(true);

  const token = () => localStorage.getItem("erue_auth_token") || "";
  const flash = (type:"ok"|"err", text:string) => {
    setMsg({type,text}); setTimeout(()=>setMsg(null),4000);
  };
  const api = useCallback(async (url:string, opts:RequestInit={}) => {
    const res = await fetch(url,{...opts,headers:{"Content-Type":"application/json",Authorization:`Bearer ${token()}`, ...(opts.headers||{})}});
    return res.json();
  },[]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cD, dD, pD] = await Promise.all([
        api("/api/fundraising/admin/campaigns"),
        api("/api/fundraising/admin/donations"),
        api("/api/fundraising/admin/pledges"),
      ]);
      if(cD.success) setCampaigns(cD.campaigns||[]);
      if(dD.success) setDonations(dD.donations||[]);
      if(pD.success) setPledges(pD.pledges||[]);
    } finally { setLoading(false); }
  },[api]);

  useEffect(()=>{ load(); },[load]);

  const saveCampaign = async () => {
    if(!cTitle.trim()) { flash("err","Title required"); return; }
    const url    = editCamp ? `/api/fundraising/admin/campaigns/${editCamp.id}` : "/api/fundraising/admin/campaigns";
    const method = editCamp ? "PUT" : "POST";
    const d = await api(url,{ method, body:JSON.stringify({
      title:cTitle, description:cDesc, type:cType,
      targetAmount:Number(cTarget)||0, currency:cCurrency,
      deadline:cDeadline||null, isActive:cActive,
    })});
    if(d.success) {
      flash("ok",editCamp?"Campaign updated":"Campaign created");
      setShowForm(false); setEditCamp(null); resetForm(); load();
    } else flash("err",d.error||"Failed");
  };

  const resetForm = () => { setCTitle(""); setCDesc(""); setCType("general"); setCTarget(""); setCCurrency("NGN"); setCDeadline(""); setCActive(true); };

  const openEdit = (c: Campaign) => {
    setEditCamp(c); setCTitle(c.title); setCDesc(c.description); setCType(c.type);
    setCTarget(String(c.targetAmount||"")); setCCurrency(c.currency); setCDeadline(c.deadline||""); setCActive(c.isActive);
    setShowForm(true);
  };

  const toggleCampaign = async (c: Campaign) => {
    const d = await api(`/api/fundraising/admin/campaigns/${c.id}`,{ method:"PUT", body:JSON.stringify({...c,isActive:!c.isActive})});
    if(d.success) { flash("ok",!c.isActive?"Campaign activated":"Campaign paused"); load(); }
    else flash("err",d.error||"Failed");
  };

  const deleteCampaign = async (id:string) => {
    if(!confirm("Delete campaign?")) return;
    const d = await api(`/api/fundraising/admin/campaigns/${id}`,{method:"DELETE"});
    if(d.success) { flash("ok","Deleted"); load(); }
    else flash("err",d.error||"Failed");
  };

  const fulfillPledge = async (id:string) => {
    const d = await api(`/api/fundraising/admin/pledges/${id}`,{method:"PUT",body:JSON.stringify({status:"fulfilled"})});
    if(d.success) { flash("ok","Pledge marked as fulfilled"); load(); }
    else flash("err",d.error||"Failed");
  };

  const fmt = (n:number,cur="NGN") =>
    new Intl.NumberFormat("en-NG",{style:"currency",currency:cur,maximumFractionDigits:0}).format(n);

  const totalRaised = donations.filter(d=>d.status==="confirmed").reduce((s,d)=>s+d.amount,0);
  const totalPledged = pledges.filter(p=>p.pledgeType==="amount").reduce((s,p)=>s+(p.amount||0),0);

  return (
    <div>
      {/* KPI row */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label:"Total Raised",   value:fmt(totalRaised),    color:"#4ADE80" },
          { label:"Total Pledged",  value:fmt(totalPledged),   color:"#60A5FA" },
          { label:"Donors",         value:String(donations.filter(d=>d.status==="confirmed").length), color:"#C9A84C" },
        ].map(({label,value,color}) => (
          <div key={label} className="p-4 rounded-xl text-center" style={{ background:"#121E16", border:"1px solid #1C2E22" }}>
            <p className="text-xs mb-1" style={{ color:"rgba(237,232,220,0.4)" }}>{label}</p>
            <p className="text-lg font-bold" style={{ color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {(["campaigns","donations","pledges"] as TabType[]).map(t=>(
          <button key={t} onClick={()=>setTab(t)}
                  className="px-4 py-2 rounded-lg text-xs font-medium transition-all border capitalize"
                  style={{ background:tab===t?"#C9A84C":"transparent", borderColor:tab===t?"#C9A84C":"#1C2E22", color:tab===t?"#070D0A":"rgba(237,232,220,0.5)" }}>
            {t}
          </button>
        ))}
        <button onClick={load} className="ml-auto p-2 rounded-lg border" style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.4)" }}>
          <RefreshCw size={13} className={loading?"animate-spin":""}/>
        </button>
      </div>

      {/* Flash */}
      {msg && (
        <div className="mb-4 px-4 py-3 rounded-xl text-sm flex items-center gap-2"
             style={{ background:msg.type==="ok"?"rgba(74,222,128,0.1)":"rgba(239,68,68,0.1)", border:`1px solid ${msg.type==="ok"?"rgba(74,222,128,0.3)":"rgba(239,68,68,0.3)"}`, color:msg.type==="ok"?"#4ADE80":"#FCA5A5" }}>
          {msg.type==="ok"?<CheckCircle size={13}/>:<AlertTriangle size={13}/>} {msg.text}
        </div>
      )}

      {/* ── CAMPAIGNS TAB ── */}
      {tab==="campaigns" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>{campaigns.length} campaigns</p>
            <button onClick={()=>{ resetForm(); setEditCamp(null); setShowForm(true); }}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold"
                    style={{ background:"#C9A84C", color:"#070D0A" }}>
              <Plus size={13}/> New Campaign
            </button>
          </div>

          {/* Campaign form */}
          {showForm && (
            <div className="mb-4 p-5 rounded-2xl" style={{ background:"#0E1812", border:"1px solid rgba(201,168,76,0.2)" }}>
              <h3 className="text-sm font-semibold mb-4" style={{ color:"#EDE8DC" }}>
                {editCamp ? "Edit Campaign" : "New Campaign"}
              </h3>
              <div className="space-y-3">
                <input value={cTitle} onChange={e=>setCTitle(e.target.value)} placeholder="Campaign title"
                       className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                       style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                <textarea value={cDesc} onChange={e=>setCDesc(e.target.value)} rows={2} placeholder="Description"
                          className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                          style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Type</label>
                    <select value={cType} onChange={e=>setCType(e.target.value as Campaign["type"])}
                            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                            style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}>
                      <option value="general">General</option>
                      <option value="event">Event</option>
                      <option value="item">Item</option>
                      <option value="village">Village</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Target (0=open)</label>
                    <input type="number" value={cTarget} onChange={e=>setCTarget(e.target.value)} placeholder="0"
                           className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                           style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                  </div>
                  <div>
                    <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Currency</label>
                    <select value={cCurrency} onChange={e=>setCCurrency(e.target.value)}
                            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                            style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}>
                      <option value="NGN">NGN (₦)</option>
                      <option value="USD">USD ($)</option>
                      <option value="GBP">GBP (£)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs mb-1 block" style={{ color:"rgba(237,232,220,0.4)" }}>Deadline</label>
                    <input type="date" value={cDeadline} onChange={e=>setCDeadline(e.target.value)}
                           className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                           style={{ background:"#121E16", border:"1px solid #1C2E22", color:"#EDE8DC" }}/>
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={cActive} onChange={e=>setCActive(e.target.checked)} className="accent-yellow-400"/>
                  <span className="text-sm" style={{ color:"rgba(237,232,220,0.6)" }}>Campaign Active</span>
                </label>
                <div className="flex gap-2">
                  <button onClick={()=>{setShowForm(false);setEditCamp(null);resetForm();}}
                          className="flex-1 py-2 rounded-lg text-xs border"
                          style={{ borderColor:"#1C2E22", color:"rgba(237,232,220,0.5)" }}>Cancel</button>
                  <button onClick={saveCampaign}
                          className="flex-1 py-2 rounded-lg text-xs font-semibold"
                          style={{ background:"#C9A84C", color:"#070D0A" }}>
                    {editCamp?"Update":"Create"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {campaigns.length === 0 ? (
            <div className="text-center py-12" style={{ color:"rgba(237,232,220,0.3)" }}>
              <Target size={32} className="mx-auto mb-2 opacity-30"/>
              No campaigns yet
            </div>
          ) : (
            <div className="space-y-3">
              {campaigns.map(c => {
                const pct = c.targetAmount>0?Math.min((c.raisedAmount/c.targetAmount)*100,100):0;
                return (
                  <div key={c.id} className="p-4 rounded-xl border" style={{ borderColor:"#1C2E22", background:"#121E16" }}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm" style={{ color:"#EDE8DC" }}>{c.title}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded" style={{ background:"rgba(201,168,76,0.1)", color:"#C9A84C" }}>{c.type}</span>
                        </div>
                        <p className="text-xs mt-0.5" style={{ color:"rgba(237,232,220,0.4)" }}>{c.description}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button onClick={()=>toggleCampaign(c)} style={{ color:c.isActive?"#4ADE80":"rgba(237,232,220,0.3)" }}>
                          {c.isActive?<ToggleRight size={20}/>:<ToggleLeft size={20}/>}
                        </button>
                        <button onClick={()=>openEdit(c)} style={{ color:"#C9A84C" }}><Edit2 size={13}/></button>
                        <button onClick={()=>deleteCampaign(c.id)} style={{ color:"rgba(239,68,68,0.5)" }}><Trash2 size={13}/></button>
                      </div>
                    </div>
                    {c.targetAmount>0 && (
                      <>
                        <div className="h-1.5 rounded-full mb-1" style={{ background:"#1C2E22" }}>
                          <div className="h-full rounded-full" style={{ width:`${pct}%`, background:"#C9A84C" }}/>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span style={{ color:"#C9A84C" }}>{fmt(c.raisedAmount,c.currency)} raised</span>
                          <span style={{ color:"rgba(237,232,220,0.3)" }}>Goal: {fmt(c.targetAmount,c.currency)}</span>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── DONATIONS TAB ── */}
      {tab==="donations" && (
        <div>
          <p className="text-xs mb-4" style={{ color:"rgba(237,232,220,0.4)" }}>
            {donations.length} total donations · {donations.filter(d=>d.status==="confirmed").length} confirmed
          </p>
          {donations.length === 0 ? (
            <div className="text-center py-12" style={{ color:"rgba(237,232,220,0.3)" }}>
              <Heart size={32} className="mx-auto mb-2 opacity-30"/>No donations yet
            </div>
          ) : (
            <div className="space-y-2">
              {donations.map(d => (
                <div key={d.id} className="flex items-center justify-between p-3 rounded-xl"
                     style={{ background:"#121E16", border:"1px solid #1C2E22" }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium" style={{ color:"#EDE8DC" }}>
                        {d.isAnonymous?"Anonymous":d.donorName}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded"
                            style={{ background:d.status==="confirmed"?"rgba(74,222,128,0.1)":"rgba(250,204,21,0.1)", color:d.status==="confirmed"?"#4ADE80":"#FDE047" }}>
                        {d.status}
                      </span>
                    </div>
                    <p className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>
                      {d.donorEmail} · {new Date(d.donatedAt).toLocaleDateString()}
                      {d.donorVillage && ` · ${d.donorVillage}`}
                    </p>
                    {d.message && <p className="text-xs italic mt-0.5" style={{ color:"rgba(237,232,220,0.3)" }}>"{d.message.slice(0,60)}"</p>}
                  </div>
                  <span className="font-bold text-sm ml-4" style={{ color:"#4ADE80" }}>
                    {fmt(d.amount,d.currency)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── PLEDGES TAB ── */}
      {tab==="pledges" && (
        <div>
          <p className="text-xs mb-4" style={{ color:"rgba(237,232,220,0.4)" }}>
            {pledges.length} pledges · {pledges.filter(p=>p.status==="pending").length} pending fulfilment
          </p>
          {pledges.length === 0 ? (
            <div className="text-center py-12" style={{ color:"rgba(237,232,220,0.3)" }}>
              <Gift size={32} className="mx-auto mb-2 opacity-30"/>No pledges yet
            </div>
          ) : (
            <div className="space-y-2">
              {pledges.map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 rounded-xl"
                     style={{ background:"#121E16", border:"1px solid #1C2E22" }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium" style={{ color:"#EDE8DC" }}>{p.name}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded"
                            style={{ background:p.pledgeType==="amount"?"rgba(201,168,76,0.1)":"rgba(96,165,250,0.1)", color:p.pledgeType==="amount"?"#C9A84C":"#60A5FA" }}>
                        {p.pledgeType}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded"
                            style={{ background:p.status==="fulfilled"?"rgba(74,222,128,0.1)":"rgba(250,204,21,0.1)", color:p.status==="fulfilled"?"#4ADE80":"#FDE047" }}>
                        {p.status}
                      </span>
                    </div>
                    <p className="text-xs" style={{ color:"rgba(237,232,220,0.4)" }}>
                      {p.pledgeType==="amount" ? fmt(p.amount||0) : p.item}
                      {p.fulfillBy && ` · By ${new Date(p.fulfillBy).toLocaleDateString()}`}
                    </p>
                  </div>
                  {p.status==="pending" && (
                    <button onClick={()=>fulfillPledge(p.id)}
                            className="ml-4 px-3 py-1.5 rounded-lg text-xs border"
                            style={{ borderColor:"rgba(74,222,128,0.3)", color:"#4ADE80" }}>
                      Mark Fulfilled
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
